import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Field, Select, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';

const TICKETS_TAB = '/assets?tab=tickets';

interface AssetOption {
  id: string;
  code: string;
  name: string;
}

export function RaiseTicketPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), TICKETS_TAB);
  const initialAssetId = params.get('assetId') ?? '';
  const [assetId, setAssetId] = useState(initialAssetId);
  const [priority, setPriority] = useState('normal');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const assets = useQuery({
    queryKey: ['assets', '', ''],
    queryFn: () => get<AssetOption[]>('/assets?limit=300'),
  });

  const raise = useMutation({
    mutationFn: () => post('/assets/tickets', { assetId, kind: 'breakdown', priority, description }),
    onSuccess: () => {
      toast.success('Reported', 'The asset is now marked out of service');
      void queryClient.invalidateQueries({ queryKey: ['maintenance-tickets'] });
      void queryClient.invalidateQueries({ queryKey: ['assets'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const valid = Boolean(assetId) && description.trim().length >= 3;
  const dirty = !raise.isSuccess && (assetId !== initialAssetId || priority !== 'normal' || description !== '');

  return (
    <FormScreen
      title="Report a breakdown"
      description="Reporting one takes the machine out of service straight away"
      icon={AlertTriangle}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) return;
        setError(null);
        raise.mutate();
      }}
    >
      <FormSection title={tr('admin', 'problem')} icon={AlertTriangle}>
        <Field label="Asset" required>
          <Select value={assetId} onChange={(e) => setAssetId(e.target.value)}>
            <option value="">Select…</option>
            {assets.data?.map((asset) => (
              <option key={asset.id} value={asset.id}>
                {asset.code} · {asset.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Priority">
          <ChoiceButtons
            layout="chips"
            value={priority}
            onChange={setPriority}
            options={['low', 'normal', 'high', 'critical'].map((p) => ({ value: p, label: humanize(p, 'priority') }))}
          />
        </Field>
        <Field label="What is wrong?" required className="sm:col-span-2">
          <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="Report" loading={raise.isPending} incomplete={!valid} cancelTo={returnTo} />
    </FormScreen>
  );
}
